/**
 * Conversations list page — capability-gated.
 *
 * Only rendered for workspaces with 'conversations' capability AND
 * transcript_policy='drilldown_allowed'. The API enforces this server-side too.
 */

import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import * as api from '../api-client';
import {
  AdminEmptyState,
  AdminPage,
  AdminPageHeader,
  AdminPanel,
} from '../components/AdminPrimitives';

interface Props {
  onViewConversation: (conversationId: string) => void;
  onVisibleConversationIdsChange?: (conversationIds: string[]) => void;
}

const PAGE_SIZE = 20;
type ConversationWindowDays = 7 | 30;

type ConversationPreview = {
  firstUserPrompt: string | null;
  firstAssistantResponse: string | null;
};

export function ConversationsPage({ onViewConversation, onVisibleConversationIdsChange }: Props) {
  const { workspace } = useWorkspace();
  const [conversations, setConversations] = useState<api.ConversationSummary[]>([]);
  const [topIntents, setTopIntents] = useState<api.ConversationIntentSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [windowDays, setWindowDays] = useState<ConversationWindowDays>(7);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, ConversationPreview>>({});
  const [selectedIntent, setSelectedIntent] = useState<string>('all');
  const [selectedRoutingTier, setSelectedRoutingTier] = useState<string>('all');

  const handleWindowChange = useCallback((value: ConversationWindowDays) => {
    setWindowDays(value);
    setOffset(0);
  }, []);

  const loadPage = useCallback(
    async (newOffset: number) => {
      if (!workspace) return;
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.listConversations(
          workspace.workspace_key,
          PAGE_SIZE,
          newOffset,
          windowDays,
        );
        setConversations(res.conversations);
        setTopIntents(res.top_intents ?? []);
        setSelectedIntent('all');
        setSelectedRoutingTier('all');
        setTotal(res.total);
        setOffset(newOffset);
      } catch (err) {
        setError(
          err instanceof api.AdminApiError ? err.detail : 'Failed to load conversations',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [workspace, windowDays],
  );

  useEffect(() => {
    void loadPage(0);
  }, [loadPage]);

  useEffect(() => {
    if (!workspace || conversations.length === 0) {
      setPreviews({});
      return;
    }

    let cancelled = false;
    const workspaceKey = workspace.workspace_key;
    setIsLoadingPreviews(true);
    setPreviews({});

    async function loadPreviews() {
      const settled = await Promise.allSettled(
        conversations.map(async (conversation) => {
          const detail = await api.getConversation(
            workspaceKey,
            conversation.conversation_id,
          );
          return {
            conversationId: conversation.conversation_id,
            preview: extractConversationPreview(detail),
          };
        }),
      );

      if (cancelled) return;

      const next: Record<string, ConversationPreview> = {};
      for (const result of settled) {
        if (result.status !== 'fulfilled') continue;
        next[result.value.conversationId] = result.value.preview;
      }
      setPreviews(next);
      setIsLoadingPreviews(false);
    }

    void loadPreviews();
    return () => {
      cancelled = true;
    };
  }, [workspace, conversations]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canonicalTopIntents = canonicalizeTopIntents(topIntents);
  const intentOptions = Array.from(
    new Set(conversations.map((conversation) => canonicalizeIntent(conversation.intent_summary))),
  )
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  const routingTierOptions = Array.from(
    new Set(conversations.map((conversation) => (conversation.routing_tier ?? 'standard').toUpperCase())),
  ).sort((a, b) => a.localeCompare(b));
  const filteredConversations = conversations.filter((conversation) => {
    const matchesIntent =
      selectedIntent === 'all' ||
      canonicalizeIntent(conversation.intent_summary) === selectedIntent;
    const routingLabel = (conversation.routing_tier ?? 'standard').toUpperCase();
    const matchesRouting = selectedRoutingTier === 'all' || routingLabel === selectedRoutingTier;
    return matchesIntent && matchesRouting;
  });

  useEffect(() => {
    onVisibleConversationIdsChange?.(filteredConversations.map((conversation) => conversation.conversation_id));
  }, [filteredConversations, onVisibleConversationIdsChange]);

  if (!workspace) return null;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Conversations"
        subtitle={`Review top intents, scan first prompts and assistant responses, and open any row to inspect the live transcript across the last ${windowDays} days.`}
        actions={
          <div className="flex items-center gap-3">
            {!isLoading ? <span className="text-sm text-[var(--text-tertiary)]">{total} total</span> : null}
            <WindowToggle value={windowDays} onChange={handleWindowChange} />
          </div>
        }
      />

      {error && (
        <div role="alert" className="mb-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[var(--status-error)]">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-20 rounded-[1.2rem] bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <AdminEmptyState
          title="No conversations found"
          description={`Conversation indexing has not produced any drill-down rows for this workspace in the last ${windowDays} days.`}
        />
      ) : (
        <>
          {canonicalTopIntents.length > 0 && (
            <AdminPanel
              title="Top 5 Question Intents"
              subtitle={`Workable v1 intent buckets across indexed conversations in the last ${windowDays} days. Raw prompts are preserved in the review queue.`}
              className="mb-6"
            >
              <div className="space-y-3">
                {canonicalTopIntents.map((item, index) => {
                  const share = total > 0 ? item.count / total : 0;
                  return (
                    <div
                      key={item.intent}
                      className="grid grid-cols-[32px_minmax(0,220px)_1fr_auto] items-center gap-4"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--surface-tertiary)] text-sm font-semibold text-[var(--text-primary)]">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                        {item.intent}
                      </span>
                      <div className="h-3 overflow-hidden rounded-full border border-[var(--border-subtle)] bg-[var(--surface-tertiary)]">
                        <div
                          className="h-full rounded-full bg-[var(--accent-visual)]"
                          style={{ width: `${Math.max(share * 100, 4)}%` }}
                        />
                      </div>
                      <span className="text-sm font-mono text-[var(--text-secondary)]">
                        {item.count} ({formatPercent(share)})
                      </span>
                    </div>
                  );
                })}
              </div>
            </AdminPanel>
          )}

          <AdminPanel
            title="Conversation Review Queue"
            subtitle={`First user prompt and first assistant response are fetched live for the current ${windowDays}-day view so you can audit responses without opening every conversation.`}
          >
            <div className="mb-5 flex flex-col gap-3 border-b border-[var(--border-subtle)] pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  Filter the current page
                </p>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Narrow the audit queue by canonical intent bucket or routing tier.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <label className="flex flex-col gap-1 text-sm text-[var(--text-secondary)]">
                  <span>Intent</span>
                  <select
                    value={selectedIntent}
                    onChange={(event) => setSelectedIntent(event.target.value)}
                    className="min-w-[220px] rounded-xl border border-[var(--border-default)] bg-white px-3 py-2 text-sm text-[var(--text-primary)] shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                  >
                    <option value="all">All intents</option>
                    {intentOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm text-[var(--text-secondary)]">
                  <span>Routing tier</span>
                  <select
                    value={selectedRoutingTier}
                    onChange={(event) => setSelectedRoutingTier(event.target.value)}
                    className="min-w-[180px] rounded-xl border border-[var(--border-default)] bg-white px-3 py-2 text-sm text-[var(--text-primary)] shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                  >
                    <option value="all">All tiers</option>
                    {routingTierOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {filteredConversations.length === 0 ? (
              <div className="rounded-[1.15rem] border border-dashed border-[var(--border-default)] bg-[var(--surface-tertiary)] px-4 py-8 text-center text-sm text-[var(--text-secondary)]">
                No conversations match the selected filters on this page.
              </div>
            ) : (
            <div className="space-y-4">
              {filteredConversations.map((c) => {
                const preview = previews[c.conversation_id];
                return (
                  <button
                    key={c.conversation_id}
                    type="button"
                    onClick={() => onViewConversation(c.conversation_id)}
                    className="w-full rounded-[1.3rem] border border-[var(--border-default)] bg-[var(--surface-secondary)] p-5 text-left shadow-[0_8px_22px_rgba(15,23,42,0.04)] transition-colors hover:bg-[var(--accent-subtle)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-[var(--text-primary)]">
                          {c.intent_summary ?? 'Uncategorized conversation'}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--text-tertiary)]">
                          <span className="rounded-full border border-[var(--border-default)] bg-white px-2.5 py-1 font-mono">
                            {c.conversation_id}
                          </span>
                          <span className="rounded-full border border-[var(--border-default)] bg-white px-2.5 py-1 font-mono">
                            User {c.user_id_hash.slice(0, 8)}
                          </span>
                          <span className="rounded-full border border-[var(--border-default)] bg-white px-2.5 py-1">
                            {formatTimestamp(c.started_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <ConversationMetaBadge label={`${c.turn_count} turns`} tone="default" />
                        <ConversationMetaBadge
                          label={(c.routing_tier ?? 'standard').toUpperCase()}
                          tone="default"
                        />
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
                      <PreviewBlock
                        title="First User Prompt"
                        text={preview?.firstUserPrompt ?? null}
                        isLoading={isLoadingPreviews && !preview}
                        tone="user"
                      />
                      <PreviewBlock
                        title="First Assistant Response"
                        text={preview?.firstAssistantResponse ?? null}
                        isLoading={isLoadingPreviews && !preview}
                        tone="assistant"
                      />
                    </div>
                  </button>
                );
              })}
            </div>
            )}
          </AdminPanel>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                onClick={() => loadPage(offset - PAGE_SIZE)}
                disabled={offset === 0}
                className="rounded-xl border border-[var(--border-default)] bg-white px-4 py-2 text-sm font-medium text-[var(--text-primary)] shadow-sm transition-colors hover:bg-[var(--interactive-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-[var(--text-secondary)]">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => loadPage(offset + PAGE_SIZE)}
                disabled={currentPage >= totalPages}
                className="rounded-xl border border-[var(--border-default)] bg-white px-4 py-2 text-sm font-medium text-[var(--text-primary)] shadow-sm transition-colors hover:bg-[var(--interactive-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </AdminPage>
  );
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function canonicalizeTopIntents(
  items: api.ConversationIntentSummary[],
): api.ConversationIntentSummary[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const canonical = canonicalizeIntent(item.intent);
    counts.set(canonical, (counts.get(canonical) ?? 0) + item.count);
  }

  return Array.from(counts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    })
    .slice(0, 5)
    .map(([intent, count]) => ({ intent, count }));
}

function canonicalizeIntent(intentSummary: string | null): string {
  const value = (intentSummary ?? '').trim();
  if (!value) return 'Other';
  const normalized = value.toLowerCase();

  if (
    normalized.includes('compare')
    || normalized.includes('difference')
    || normalized.includes('vs')
  ) {
    return 'Product Comparison';
  }
  if (
    normalized.includes('return')
    || normalized.includes('exchange')
    || normalized.includes('refund')
  ) {
    return 'Returns & Exchanges';
  }
  if (
    normalized.includes('shipping')
    || normalized.includes('delivery')
    || normalized.includes('arrive')
  ) {
    return 'Shipping & Delivery';
  }
  if (
    normalized.includes('price')
    || normalized.includes('discount')
    || normalized.includes('sale')
    || normalized.includes('promo')
  ) {
    return 'Pricing & Promotions';
  }
  if (
    normalized.includes('order')
    || normalized.includes('support')
    || normalized.includes('tracking')
    || normalized.includes('status')
  ) {
    return 'Order Support';
  }
  if (
    normalized.includes('compatib')
    || normalized.includes('size')
    || normalized.includes('material')
    || normalized.includes('detail')
  ) {
    return 'Product Details & Compatibility';
  }
  if (
    normalized.includes('policy')
    || normalized.includes('faq')
    || normalized.includes('store')
  ) {
    return 'Store Policy & FAQ';
  }
  if (
    normalized.includes('hot sleeper')
    || normalized.includes('sleep')
    || normalized.includes('cooling')
  ) {
    return 'Sleep Guidance & FAQ';
  }
  if (
    normalized === 'product_discovery'
    || normalized === 'recommendations_gifting'
    || normalized.includes('best')
    || normalized.includes('recommend')
    || normalized.includes('which ')
  ) {
    return 'Product Recommendation';
  }
  if (normalized === 'pricing_promotions') return 'Pricing & Promotions';
  if (normalized === 'shipping_returns') return 'Returns & Exchanges';
  if (normalized === 'product_details_compatibility') return 'Product Details & Compatibility';
  if (normalized === 'sleep_science') return 'Sleep Guidance & FAQ';
  if (normalized === 'store_policy') return 'Store Policy & FAQ';
  if (normalized === 'order_status_support') return 'Order Support';

  return 'Other';
}

function extractConversationPreview(detail: api.ConversationDetail): ConversationPreview {
  const firstUserTurn = detail.turns.find((turn) => Boolean(turn.user_message));
  const firstAssistantTurn = detail.turns.find((turn) => Boolean(turn.assistant_message));
  return {
    firstUserPrompt: firstUserTurn?.user_message?.trim() ?? null,
    firstAssistantResponse: firstAssistantTurn?.assistant_message?.trim() ?? null,
  };
}

function truncatePreview(text: string, maxLength = 180): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1)}…`;
}

function PreviewBlock({
  title,
  text,
  isLoading,
  tone,
}: {
  title: string;
  text: string | null;
  isLoading: boolean;
  tone: 'user' | 'assistant';
}) {
  const toneClasses =
    tone === 'user'
      ? 'border-[var(--border-default)] bg-white'
      : 'border-[var(--border-default)] bg-[var(--accent-subtle)]';

  return (
    <div className={`rounded-[1.15rem] border px-4 py-4 ${toneClasses}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
        {title}
      </p>
      {isLoading ? (
        <div className="mt-3 h-12 rounded-lg bg-[var(--surface-tertiary)] animate-pulse" />
      ) : text ? (
        <p className="mt-3 text-sm leading-6 text-[var(--text-primary)]">
          {truncatePreview(text)}
        </p>
      ) : (
        <p className="mt-3 text-sm text-[var(--text-tertiary)]">Preview unavailable.</p>
      )}
    </div>
  );
}

function ConversationMetaBadge({
  label,
  tone: _tone,
}: {
  label: string;
  tone: 'default' | 'info';
}) {
  return (
    <span
      className="inline-flex items-center rounded-full border border-[var(--border-default)] bg-white px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-secondary)]"
    >
      {label}
    </span>
  );
}

function WindowToggle({
  value,
  onChange,
}: {
  value: ConversationWindowDays;
  onChange: (value: ConversationWindowDays) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-full border border-[var(--border-default)] bg-white p-1 shadow-sm">
      {([
        { label: 'Last 7 Days', value: 7 as ConversationWindowDays },
        { label: 'Last 30 Days', value: 30 as ConversationWindowDays },
      ]).map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={[
              'rounded-full px-4 py-2 text-sm font-medium transition-colors',
              active
                ? 'bg-[var(--text-primary)] text-white shadow-[0_4px_12px_rgba(15,23,42,0.18)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            ].join(' ')}
            aria-pressed={active}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
